import type { Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { escapeHtml } from "../../ui/text.ts";
import { bindDestinationPicker } from "../../ui/destination-picker.ts";

export interface SubtaskTransferCapabilities {
  entities(): Promise<StoredEntity[]>;
  roots(): Root[];
  canEdit(todo: Todo, rootId: string): boolean;
  actorId(): string;
  randomId(): string;
  changeTodo(id: string, rootId: string, actorId: string, update: (todo: Todo) => void): Promise<unknown>;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  setLocal(): void;
  render(): void | Promise<void>;
  undo(message: string, action: () => void | Promise<void>): void;
  sync(): void | Promise<void>;
}

export async function transferSubtask(sourceTaskId: string, subtaskId: string, capabilities: SubtaskTransferCapabilities): Promise<void> {
  const entities = await capabilities.entities();
  const sourceEntity = entities.find(entity => entity.kind === "todo" && entity.id === sourceTaskId);
  const source = sourceEntity?.projection as Todo | undefined;
  const subtask = source?.subtasks?.find(item => item.id === subtaskId);
  const sourceIndex = source?.subtasks?.findIndex(item => item.id === subtaskId) ?? -1;
  if (!sourceEntity || !source || !subtask || !capabilities.canEdit(source, sourceEntity.rootId)) { capabilities.toast("This subtask can no longer be moved", true); return; }

  const categoryNames = new Map(entities.filter(entity => entity.kind === "category").map(entity => { const category = entity.projection as Category; return [category.id, category.name] as const; }));
  const candidates = entities
    .filter(entity => entity.kind === "todo" && entity.id !== sourceTaskId)
    .map(entity => ({ entity, todo: entity.projection as Todo }))
    .filter(({ entity, todo }) => !todo.deleted && capabilities.canEdit(todo, entity.rootId))
    .sort((left, right) => {
      const leftRoot = capabilities.roots().find(root => root.id === left.entity.rootId)?.name || "Space";
      const rightRoot = capabilities.roots().find(root => root.id === right.entity.rootId)?.name || "Space";
      return leftRoot.localeCompare(rightRoot) || (categoryNames.get(left.todo.categoryId || "") || "").localeCompare(categoryNames.get(right.todo.categoryId || "") || "") || left.todo.title.localeCompare(right.todo.title);
    });
  if (!candidates.length) { capabilities.toast("Create another editable task before moving this subtask", true); return; }
  const grouped = new Map<string, typeof candidates>();
  candidates.forEach(candidate => {
    const rootName = capabilities.roots().find(root => root.id === candidate.entity.rootId)?.name || "Shared space";
    const collection = categoryNames.get(candidate.todo.categoryId || "") || "No collection";
    const label = `${rootName} → ${collection}`;
    grouped.set(label, [...(grouped.get(label) || []), candidate]);
  });

  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog move-subtask-dialog";
  dialog.innerHTML = `<div class="share-dialog__header"><div><p class="eyebrow">Move or copy</p><h2>${escapeHtml(subtask.title)}</h2><p>Choose another task for this subtask.</p></div><button class="icon-button" type="button" data-move-close aria-label="Close">${capabilities.icon("close")}</button></div><form><label class="field-label">Destination task<select name="destination" required>${[...grouped.entries()].map(([label, items]) => `<optgroup label="${escapeHtml(label)}">${items.map(({ todo }) => `<option value="${todo.id}">${escapeHtml(todo.title || "Untitled task")}</option>`).join("")}</optgroup>`).join("")}</select></label><p class="field-note">The title, note, and completion state will be preserved.</p><div class="dialog-actions"><button class="button button--soft" type="button" data-move-close>Cancel</button><button class="button button--soft" type="submit" name="operation" value="copy">Copy</button><button class="button button--primary" type="submit" name="operation" value="move">Move</button></div></form>`;
  bindDestinationPicker(dialog.querySelector<HTMLSelectElement>('[name="destination"]')!);
  dialog.querySelectorAll<HTMLElement>("[data-move-close]").forEach(button => button.addEventListener("click", () => dialog.close()));
  dialog.querySelector("form")!.addEventListener("submit", async event => {
    event.preventDefault();
    const operation = ((event as SubmitEvent).submitter as HTMLButtonElement | null)?.value === "copy" ? "copy" : "move";
    const destinationId = String(new FormData(event.currentTarget as HTMLFormElement).get("destination") || "");
    const destination = candidates.find(candidate => candidate.todo.id === destinationId);
    if (!destination || !capabilities.canEdit(destination.todo, destination.entity.rootId)) { capabilities.toast("That destination is no longer editable", true); return; }
    const submitButtons = dialog.querySelectorAll<HTMLButtonElement>('button[type="submit"]');
    submitButtons.forEach(button => { button.disabled = true; });
    const transferredSubtask = structuredClone(subtask);
    if (operation === "copy") transferredSubtask.id = capabilities.randomId();
    try {
      await capabilities.changeTodo(destination.todo.id, destination.entity.rootId, capabilities.actorId(), todo => { todo.type = "checklist"; todo.subtasks ||= []; if (!todo.subtasks.some(item => item.id === transferredSubtask.id)) todo.subtasks.push(transferredSubtask); });
      if (operation === "move") {
        try {
          await capabilities.changeTodo(source.id, sourceEntity.rootId, capabilities.actorId(), todo => { todo.subtasks = (todo.subtasks || []).filter(item => item.id !== subtask.id); });
        } catch (error) {
          await capabilities.changeTodo(destination.todo.id, destination.entity.rootId, capabilities.actorId(), todo => { todo.subtasks = (todo.subtasks || []).filter(item => item.id !== transferredSubtask.id); });
          throw error;
        }
      }
      capabilities.setLocal();
      dialog.close();
      await capabilities.render();
      capabilities.undo(`${operation === "copy" ? "Copied" : "Moved"} to ${destination.todo.title || "Untitled task"}`, async () => {
        await capabilities.changeTodo(destination.todo.id, destination.entity.rootId, capabilities.actorId(), todo => { todo.subtasks = (todo.subtasks || []).filter(item => item.id !== transferredSubtask.id); });
        if (operation === "move") await capabilities.changeTodo(source.id, sourceEntity.rootId, capabilities.actorId(), todo => { todo.type = "checklist"; todo.subtasks ||= []; if (!todo.subtasks.some(item => item.id === subtask.id)) todo.subtasks.splice(Math.min(sourceIndex, todo.subtasks.length), 0, structuredClone(subtask)); });
      });
      void capabilities.sync();
    } catch {
      submitButtons.forEach(button => { button.disabled = false; });
      capabilities.toast(`The subtask could not be ${operation === "copy" ? "copied" : "moved"}`, true);
    }
  });
  document.body.append(dialog);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
}
